import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { ReactComponent as ChevronLeftIcon } from '../../../shared/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../../../shared/icons/chevron-right.svg';
import LoadingElement from '../../loading-element';
import { ReactComponent as DocumentIcon } from '../../../shared/icons/document-icon.svg';
import { ReactComponent as FolderIcon } from '../../../shared/icons/folder-icon.svg';
import { ReactComponent as SearchIcon } from '../../../shared/icons/search-icon.svg';
import { ReactComponent as HomeIcon } from '../../../shared/icons/home-icon.svg';
import styles from './DocumentPicker.module.scss';

// Get deals for folder navigation
const GET_DEALS = gql`
  query GetDealsForNavigation($teamId: ID!) {
    dealsForTeam(teamId: $teamId) {
      id
      title
    }
  }
`;

// Get documents by deal
const GET_DOCUMENTS_BY_DEAL = gql`
  query GetDocumentsByDealPicker($dealId: ID!) {
    getDocumentsByDeal(dealId: $dealId) {
      id
      title
      status
      importedAssetContentType
      deal {
        id
        title
      }
    }
  }
`;

interface Document {
  id: string;
  title: string;
  status: string;
  importedAssetContentType?: string;
  deal?: {
    id: string;
    title: string;
  };
}


interface DocumentPickerProps {
  onDocumentSelect: (documentId: string, documentName: string, contentType?: string) => void;
  onBack: () => void;
  teamId: string;
}

export const DocumentPicker: React.FC<DocumentPickerProps> = ({
  onDocumentSelect,
  onBack,
  teamId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [selectedDealTitle, setSelectedDealTitle] = useState<string>('');
  const [viewMode, setViewMode] = useState<'deals' | 'documents'>('deals');
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const apolloClient = useApolloClient();

  // Get all deals for navigation
  const { data: dealsData, loading: dealsLoading } = useQuery(GET_DEALS, {
    variables: { teamId },
    skip: !teamId,
  });

  // Get documents for selected deal
  const { data: documentsData, loading: documentsLoading } = useQuery(GET_DOCUMENTS_BY_DEAL, {
    variables: { dealId: selectedDealId },
    skip: !selectedDealId,
  });

  const deals = useMemo(() => dealsData?.dealsForTeam || [], [dealsData?.dealsForTeam]);
  const documents = documentsData?.getDocumentsByDeal || [];

  // Fetch all documents when deals are loaded (for search)
  useEffect(() => {
    if (deals.length > 0 && searchQuery) {
      const fetchAllDocs = async () => {
        const allDocs: Document[] = [];
        for (const deal of deals) {
          try {
            const result = await apolloClient.query({
              query: GET_DOCUMENTS_BY_DEAL,
              variables: { dealId: deal.id }
            });
            if (result.data?.getDocumentsByDeal) {
              allDocs.push(...result.data.getDocumentsByDeal);
            }
          } catch (error) {
            // Continue with other deals
          }
        }
        setAllDocuments(allDocs);
      };
      fetchAllDocs();
    }
  }, [deals, searchQuery, apolloClient]);

  // If searching, show all matching documents; otherwise show folder structure
  const searchResults = searchQuery
    ? allDocuments.filter((doc: Document) =>
        doc.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const filteredDocuments = documents.filter((doc: Document) => {
    if (!searchQuery) return true;
    return doc.title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleDealSelect = (dealId: string) => {
    const deal = deals.find((d: any) => d.id === dealId);
    setSelectedDealId(dealId);
    setSelectedDealTitle(deal?.title || '');
    setViewMode('documents');
    setSearchQuery(''); // Clear search when navigating
  };

  const handleBackToDeals = () => {
    setViewMode('deals');
    setSelectedDealId(null);
    setSelectedDealTitle('');
    setSearchQuery('');
  };

  const handleDocumentSelect = (document: Document) => {
    onDocumentSelect(document.id, document.title, document.importedAssetContentType);
  };

  return (
    <div className={styles.documentPicker}>
      {/* Header */}
      <div className={styles.header}>
        <button
          onClick={viewMode === 'documents' ? handleBackToDeals : onBack}
          className={styles.backButton}
          aria-label="Go back"
        >
          <ChevronLeftIcon />
          <span>Back</span>
        </button>
        <div className={styles.divider}>|</div>
        <h3>Select a Document</h3>
      </div>

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <SearchIcon className={styles.searchIcon} />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
      </div>

      {/* Breadcrumb for documents view */}
      {viewMode === 'documents' && selectedDealTitle && (
        <div className={styles.breadcrumb}>
          <button
            className={styles.breadcrumbItem}
            onClick={handleBackToDeals}
            aria-label="Back to deals"
          >
            <HomeIcon className={styles.breadcrumbIcon} />
            <span>Deals</span>
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>{selectedDealTitle}</span>
        </div>
      )}

      {/* Content */}
      <div className={styles.contentList}>
        {searchQuery ? (
          // Show search results
          searchResults.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No documents found matching "{searchQuery}"</p>
            </div>
          ) : (
            <div className={styles.itemsList}>
              {searchResults.map((doc: Document) => (
                <button
                  key={doc.id}
                  className={styles.listItem}
                  onClick={() => handleDocumentSelect(doc)}
                >
                  <div className={styles.itemIcon}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemTitle}>{doc.title}</div>
                    <div className={styles.itemMeta}>
                      {doc.deal?.title && (
                        <span className={styles.dealTag}>{doc.deal.title}</span>
                      )}
                      <span>{doc.status}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : viewMode === 'deals' ? (
          // Deals view
          dealsLoading ? (
            <LoadingElement />
          ) : deals.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No deals available</p>
            </div>
          ) : (
            <div className={styles.itemsList}>
              {deals.map((deal: any) => (
                <button
                  key={deal.id}
                  className={styles.listItem}
                  onClick={() => handleDealSelect(deal.id)}
                >
                  <div className={styles.itemIcon}>
                    <FolderIcon />
                  </div>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemTitle}>{deal.title}</div>
                    <div className={styles.itemMeta}>Deal</div>
                  </div>
                  <ChevronRightIcon className={styles.chevronIcon} />
                </button>
              ))}
            </div>
          )
        ) : (
          // Documents view
          documentsLoading ? (
            <LoadingElement />
          ) : filteredDocuments.length === 0 ? (
            <div className={styles.emptyState}>
              {searchQuery ? (
                <p>No documents found matching "{searchQuery}"</p>
              ) : (
                <p>No documents in this deal</p>
              )}
            </div>
          ) : (
            <div className={styles.itemsList}>
              {filteredDocuments.map((doc: Document) => (
                <button
                  key={doc.id}
                  className={styles.listItem}
                  onClick={() => handleDocumentSelect(doc)}
                >
                  <div className={styles.itemIcon}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemTitle}>{doc.title}</div>
                    <div className={styles.itemMeta}>{doc.status}</div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};